import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import {
  Add,
  ArrowDropDownCircleOutlined,
  CheckBoxOutlined,
  Close,
  DeleteOutline,
  EditOutlined,
  LinearScale,
  PreviewOutlined,
  RadioButtonCheckedOutlined,
  StarOutline,
  SubjectOutlined,
  TableChartOutlined,
} from "@mui/icons-material";

import SearchSelect, { OptionItem } from "../../atoms/SearchSelect/SearchSelect";
import {
  createSatisfactionSurveyQuestion,
  createSatisfactionSurveyTemplate,
  deleteSatisfactionSurveyQuestion,
  findSatisfactionSurveyQuestionOptions,
  findSatisfactionSurveySourceColumns,
  findSatisfactionSurveyTemplate,
  updateSatisfactionSurveyQuestion,
  updateSatisfactionSurveyTemplate,
} from "../../../API/apiSatisfactionSurveyTemplates";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import styles from "./CadastrarModeloPesquisa.module.css";
import certStyles from "../Certificados/Certificados.module.css";
import stylesGrid from "../../../styles/Grid.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesButton from "../../../styles/Button.module.css";
import stylesGeneral from "../../../styles/General.module.css";
import {
  SatisfactionQuestionFilterOperator,
  SatisfactionQuestionTableConfig,
  SatisfactionQuestionType,
  SatisfactionSurveyQuestion,
  SatisfactionSurveyQuestionPayload,
  SatisfactionSurveySourceColumn,
  SatisfactionSurveySourceTable,
  SatisfactionSurveyTemplate,
} from "../../../types/SatisfactionSurveyTemplate";

type QuestionOptionDraft = {
  localId: string;
  label: string;
};

type QuestionFilterDraft = {
  localId: string;
  field: string;
  operator: SatisfactionQuestionFilterOperator;
  value: string;
};

type QuestionDraft = {
  localId: string;
  id?: number;
  nome: string;
  ordem: number;
  tipo: SatisfactionQuestionType;
  obrigatorio: boolean;
  active: boolean;
  opcoes: QuestionOptionDraft[];
  config: {
    source: SatisfactionSurveySourceTable | "";
    value_column: string;
    label_column: string;
    order_by: string;
    order_direction: "asc" | "desc";
    limit: string;
    filters: QuestionFilterDraft[];
  };
};

type LocationState = {
  template?: SatisfactionSurveyTemplate;
};

type QuestionOptionsMap = Record<number, Array<{ value: string | number; label: string }>>;
type QuestionModalStep = "select-type" | "edit";

const questionTypeOptions: Array<{ value: SatisfactionQuestionType; label: string }> = [
  { value: "texto_livre", label: "Texto livre" },
  { value: "escala", label: "Escala (Péssimo ao Ótimo)" },
  { value: "escala_dez", label: "Escala 1 a 10" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio" },
  { value: "lista_personalizada", label: "Lista personalizada" },
  { value: "tabela", label: "Tabela" },
];

const getQuestionTypeIcon = (type: SatisfactionQuestionType) => {
  switch (type) {
    case "texto_livre":
      return <SubjectOutlined />;
    case "escala":
      return <LinearScale />;
    case "escala_dez":
      return <StarOutline />;
    case "checkbox":
      return <CheckBoxOutlined />;
    case "radio":
      return <RadioButtonCheckedOutlined />;
    case "lista_personalizada":
      return <ArrowDropDownCircleOutlined />;
    case "tabela":
      return <TableChartOutlined />;
    default:
      return <SubjectOutlined />;
  }
};

const tableSourceOptions: SatisfactionQuestionTableConfig["source"][] = [
  "acoes",
  "turmas",
  "modalidades",
  "tipologias",
  "temas",
  "municipios",
  "institutions",
];

const filterOperatorOptions: SatisfactionQuestionFilterOperator[] = ["=", "!=", "like", "in"];
const statusOptions: OptionItem[] = [
  { id: 1, nome: "Ativo" },
  { id: 2, nome: "Inativo" },
];
const yesNoOptions: OptionItem[] = [
  { id: 1, nome: "Sim" },
  { id: 2, nome: "Não" },
];
const tableSourceSelectOptions: OptionItem[] = tableSourceOptions.map((source, index) => ({
  id: index + 1,
  nome: source,
}));
const filterOperatorSelectOptions: OptionItem[] = filterOperatorOptions.map((operator, index) => ({
  id: index + 1,
  nome: operator,
}));
const orderDirectionOptions: OptionItem[] = [
  { id: 1, nome: "asc" },
  { id: 2, nome: "desc" },
];
const escalaOptions = [
  { value: 1, label: "Péssimo" },
  { value: 2, label: "Ruim" },
  { value: 3, label: "Regular" },
  { value: 4, label: "Bom" },
  { value: 5, label: "Ótimo" },
];

const createLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeOptionValue = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "");

const createEmptyOption = (): QuestionOptionDraft => ({
  localId: createLocalId(),
  label: "",
});

const createEmptyFilter = (): QuestionFilterDraft => ({
  localId: createLocalId(),
  field: "",
  operator: "=",
  value: "",
});

const createDefaultQuestion = (ordem: number): QuestionDraft => ({
  localId: createLocalId(),
  nome: "",
  ordem,
  tipo: "texto_livre",
  obrigatorio: true,
  active: true,
  opcoes: [createEmptyOption()],
  config: {
    source: "",
    value_column: "",
    label_column: "",
    order_by: "",
    order_direction: "asc",
    limit: "",
    filters: [createEmptyFilter()],
  },
});

const parsePrimitiveValue = (value: string) => {
  const trimmedValue = value.trim();

  if (trimmedValue === "") return "";
  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) return Number(trimmedValue);
  if (trimmedValue.toLowerCase() === "true") return true;
  if (trimmedValue.toLowerCase() === "false") return false;

  return trimmedValue;
};

const isOptionQuestionType = (tipo: SatisfactionQuestionType) =>
  ["checkbox", "radio", "lista_personalizada"].includes(tipo);

const isTableQuestionType = (tipo: SatisfactionQuestionType) => tipo === "tabela";

const normalizeQuestionToDraft = (question: SatisfactionSurveyQuestion): QuestionDraft => ({
  localId: createLocalId(),
  id: question.id,
  nome: question.nome,
  ordem: question.ordem,
  tipo: question.tipo,
  obrigatorio: question.obrigatorio,
  active: question.active,
  opcoes:
    question.opcoes && question.opcoes.length > 0
      ? question.opcoes.map((option) => ({
          localId: createLocalId(),
          label: option.label,
        }))
      : [createEmptyOption()],
  config: {
    source: question.config?.source || "",
    value_column: question.config?.value_column || "",
    label_column: question.config?.label_column || "",
    order_by: question.config?.order_by || "",
    order_direction: question.config?.order_direction || "asc",
    limit: question.config?.limit ? String(question.config.limit) : "",
    filters:
      question.config?.filters && question.config.filters.length > 0
        ? question.config.filters.map((filter) => ({
            localId: createLocalId(),
            field: filter.field,
            operator: filter.operator,
            value: Array.isArray(filter.value)
              ? filter.value.join(", ")
              : String(filter.value ?? ""),
          }))
        : [createEmptyFilter()],
  },
});

const buildQuestionPayload = (question: QuestionDraft): SatisfactionSurveyQuestionPayload => {
  const payload: SatisfactionSurveyQuestionPayload = {
    nome: question.nome.trim(),
    ordem: question.ordem,
    tipo: question.tipo,
    obrigatorio: question.obrigatorio,
    active: question.active,
  };

  if (isOptionQuestionType(question.tipo)) {
    const options = question.opcoes
      .map((option) => option.label.trim())
      .filter(Boolean)
      .map((label) => ({
        label,
        value: normalizeOptionValue(label),
      }))
      .filter((option) => option.value);

    if (options.length > 0) {
      payload.opcoes = options;
    }
  }

  if (isTableQuestionType(question.tipo) && question.config.source) {
    const filters = question.config.filters
      .filter((filter) => filter.field.trim() && filter.value.trim())
      .map((filter) => ({
        field: filter.field.trim(),
        operator: filter.operator,
        value:
          filter.operator === "in"
            ? filter.value
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean)
                .map((item) => parsePrimitiveValue(item))
            : parsePrimitiveValue(filter.value),
      }));

    payload.config = {
      source: question.config.source,
      value_column: question.config.value_column.trim(),
      label_column: question.config.label_column.trim(),
      ...(question.config.order_by.trim() ? { order_by: question.config.order_by.trim() } : {}),
      ...(question.config.order_direction ? { order_direction: question.config.order_direction } : {}),
      ...(question.config.limit.trim() ? { limit: Number(question.config.limit) } : {}),
      filters,
    };
  }

  return payload;
};

const getOptionIdByName = (options: OptionItem[], nome: string) =>
  options.find((option) => option.nome === nome)?.id || null;

const getOptionNameById = (options: OptionItem[], id: number | null) =>
  options.find((option) => option.id === id)?.nome || null;

const sortQuestions = (items: QuestionDraft[]) =>
  [...items].sort((a, b) => a.ordem - b.ordem);

const CadastrarModeloPesquisa = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("id");
  const isEditing = Boolean(templateId);
  const locationState = location.state as LocationState | null;

  const [titulo, setTitulo] = useState("Pesquisa de satisfacao");
  const [descricao, setDescricao] = useState("Pesquisa aplicada ao final da turma");
  const [mensagem, setMensagem] = useState("Sua opiniao e importante para melhorarmos nossas acoes.");
  const [active, setActive] = useState(true);
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [sourceColumnsByTable, setSourceColumnsByTable] = useState<
    Partial<Record<SatisfactionSurveySourceTable, SatisfactionSurveySourceColumn[]>>
  >({});
  const [loadingColumnsByTable, setLoadingColumnsByTable] = useState<
    Partial<Record<SatisfactionSurveySourceTable, boolean>>
  >({});
  const [questionOptionsPreview, setQuestionOptionsPreview] = useState<QuestionOptionsMap>({});
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [editingQuestionLocalId, setEditingQuestionLocalId] = useState<string | null>(null);
  const [questionDraft, setQuestionDraft] = useState<QuestionDraft | null>(null);
  const [questionModalStep, setQuestionModalStep] = useState<QuestionModalStep>("select-type");
  const [loadingQuestionAction, setLoadingQuestionAction] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedQuestionForDelete, setSelectedQuestionForDelete] = useState<QuestionDraft | null>(null);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const loadTemplateData = async (showLoading = false, forceApi = false) => {
    if (!isEditing) return;

    if (showLoading) {
      setLoadingInitialData(true);
    }

    try {
      const template =
        !forceApi && locationState?.template
          ? locationState.template
          : (templateId ? await findSatisfactionSurveyTemplate(templateId) : null);

      if (!template) {
        setMsgError("Nao foi possivel localizar o modelo de pesquisa para edicao.");
        return;
      }

      setTitulo(template.titulo);
      setDescricao(template.descricao || "");
      setMensagem(template.mensagem || "");
      setActive(template.active);
      setQuestions(
        template.questions && template.questions.length > 0
          ? sortQuestions(template.questions.map(normalizeQuestionToDraft))
          : [],
      );
    } catch {
      setMsgError("Nao foi possivel carregar o modelo de pesquisa.");
    } finally {
      if (showLoading) {
        setLoadingInitialData(false);
      }
    }
  };

  useEffect(() => {
    const loadTemplate = async () => {
      setLoadingInitialData(true);

      try {
        if (!isEditing) {
          setLoadingInitialData(false);
          return;
        }

        await loadTemplateData();
      } finally {
        setLoadingInitialData(false);
      }
    };

    void loadTemplate();
  }, [isEditing, locationState?.template, templateId]);

  const loadSourceColumns = async (source: SatisfactionSurveySourceTable) => {
    if (sourceColumnsByTable[source]?.length) return;

    setLoadingColumnsByTable((current) => ({ ...current, [source]: true }));

    try {
      const columns = await findSatisfactionSurveySourceColumns(source);
      setSourceColumnsByTable((current) => ({
        ...current,
        [source]: columns,
      }));
    } catch {
      setMsgError(`Nao foi possivel carregar as colunas da tabela ${source}.`);
    } finally {
      setLoadingColumnsByTable((current) => ({ ...current, [source]: false }));
    }
  };

  useEffect(() => {
    const sources = Array.from(
      new Set(
        questions
          .map((question) => question.config.source)
          .filter((source): source is SatisfactionSurveySourceTable => Boolean(source)),
      ),
    );

    sources.forEach((source) => {
      if (!sourceColumnsByTable[source] && !loadingColumnsByTable[source]) {
        loadSourceColumns(source);
      }
    });
  }, [questions, sourceColumnsByTable, loadingColumnsByTable]);

  useEffect(() => {
    const loadPreviewOptions = async () => {
      if (!templateId) return;

      const tableQuestions = questions.filter((question) => question.tipo === "tabela" && question.id);
      if (tableQuestions.length === 0) {
        setQuestionOptionsPreview({});
        return;
      }

      const entries = await Promise.all(
        tableQuestions.map(async (question) => {
          try {
            const options = await findSatisfactionSurveyQuestionOptions(templateId, question.id || 0);
            return [question.id || 0, options] as const;
          } catch {
            return [question.id || 0, []] as const;
          }
        }),
      );

      setQuestionOptionsPreview(Object.fromEntries(entries));
    };

    void loadPreviewOptions();
  }, [questions, templateId]);

  const openNewQuestionModal = () => {
    setEditingQuestionLocalId(null);
    setQuestionDraft(createDefaultQuestion(questions.length + 1));
    setQuestionModalStep("select-type");
    setQuestionModalOpen(true);
  };

  const openEditQuestionModal = (question: QuestionDraft) => {
    setEditingQuestionLocalId(question.localId);
    setQuestionDraft({
      ...question,
      opcoes: question.opcoes.length > 0 ? question.opcoes.map((option) => ({ ...option })) : [createEmptyOption()],
      config: {
        ...question.config,
        filters:
          question.config.filters.length > 0
            ? question.config.filters.map((filter) => ({ ...filter }))
            : [createEmptyFilter()],
      },
    });
    setQuestionModalStep("edit");
    setQuestionModalOpen(true);
  };

  const closeQuestionModal = () => {
    setQuestionModalOpen(false);
    setEditingQuestionLocalId(null);
    setQuestionDraft(null);
    setQuestionModalStep("select-type");
  };

  const updateQuestionDraft = (updater: (current: QuestionDraft) => QuestionDraft) => {
    setQuestionDraft((current) => (current ? updater(current) : current));
  };

  const selectQuestionType = (tipo: SatisfactionQuestionType) => {
    updateQuestionDraft((current) => ({
      ...current,
      tipo,
      opcoes: isOptionQuestionType(tipo)
        ? current.opcoes.length > 0
          ? current.opcoes
          : [createEmptyOption()]
        : [createEmptyOption()],
    }));
    setQuestionModalStep("edit");
  };

  const addDraftOption = () => {
    updateQuestionDraft((current) => ({
      ...current,
      opcoes: [...current.opcoes, createEmptyOption()],
    }));
  };

  const updateDraftOption = (optionLocalId: string, label: string) => {
    updateQuestionDraft((current) => ({
      ...current,
      opcoes: current.opcoes.map((option) =>
        option.localId === optionLocalId ? { ...option, label } : option,
      ),
    }));
  };

  const removeDraftOption = (optionLocalId: string) => {
    updateQuestionDraft((current) => ({
      ...current,
      opcoes:
        current.opcoes.filter((option) => option.localId !== optionLocalId).length > 0
          ? current.opcoes.filter((option) => option.localId !== optionLocalId)
          : [createEmptyOption()],
    }));
  };

  const addDraftFilter = () => {
    updateQuestionDraft((current) => ({
      ...current,
      config: {
        ...current.config,
        filters: [...current.config.filters, createEmptyFilter()],
      },
    }));
  };

  const updateDraftFilter = (
    filterLocalId: string,
    field: "field" | "operator" | "value",
    value: string,
  ) => {
    updateQuestionDraft((current) => ({
      ...current,
      config: {
        ...current.config,
        filters: current.config.filters.map((filter) =>
          filter.localId === filterLocalId
            ? {
                ...filter,
                [field]: field === "operator"
                  ? (value as SatisfactionQuestionFilterOperator)
                  : value,
              }
            : filter,
        ),
      },
    }));
  };

  const removeDraftFilter = (filterLocalId: string) => {
    updateQuestionDraft((current) => ({
      ...current,
      config: {
        ...current.config,
        filters:
          current.config.filters.filter((filter) => filter.localId !== filterLocalId).length > 0
            ? current.config.filters.filter((filter) => filter.localId !== filterLocalId)
            : [createEmptyFilter()],
      },
    }));
  };

  const validateQuestions = (items: QuestionDraft[]) => {
    for (const question of items) {
      if (!question.nome.trim()) {
        setMsgError(`A pergunta ${question.ordem} precisa informar o enunciado.`);
        return false;
      }

      if (isOptionQuestionType(question.tipo)) {
        const validOptions = question.opcoes
          .map((option) => option.label.trim())
          .filter(Boolean)
          .map((label) => normalizeOptionValue(label))
          .filter(Boolean);

        if (validOptions.length === 0) {
          setMsgError(`A pergunta "${question.nome}" precisa ter ao menos uma opção válida.`);
          return false;
        }
      }

      if (isTableQuestionType(question.tipo)) {
        if (!question.config.source) {
          setMsgError(`A pergunta "${question.nome}" precisa informar o source.`);
          return false;
        }

        if (!question.config.value_column.trim()) {
          setMsgError(`A pergunta "${question.nome}" precisa informar o campo value.`);
          return false;
        }

        if (!question.config.label_column.trim()) {
          setMsgError(`A pergunta "${question.nome}" precisa informar o campo label.`);
          return false;
        }
      }
    }

    return true;
  };

  const saveQuestionModal = async () => {
    if (!questionDraft) return;

    const nextQuestion = {
      ...questionDraft,
      nome: questionDraft.nome.trim(),
      opcoes: isOptionQuestionType(questionDraft.tipo)
        ? questionDraft.opcoes
        : [createEmptyOption()],
    };

    if (!validateQuestions([nextQuestion])) {
      return;
    }

    if (isEditing && templateId && nextQuestion.id) {
      try {
        setLoadingQuestionAction(true);
        await updateSatisfactionSurveyQuestion(templateId, nextQuestion.id, buildQuestionPayload(nextQuestion));
        await loadTemplateData(false, true);
        setMsgSuccess("Pergunta atualizada com sucesso.");
        closeQuestionModal();
      } catch {
        setMsgError("Nao foi possivel atualizar a pergunta.");
      } finally {
        setLoadingQuestionAction(false);
      }
      return;
    }

    setQuestions((current) => {
      const nextQuestions = editingQuestionLocalId
        ? current.map((question) =>
            question.localId === editingQuestionLocalId ? nextQuestion : question,
          )
        : [...current, nextQuestion];

      return sortQuestions(nextQuestions);
    });

    closeQuestionModal();
  };

  const removeQuestion = (localId: string) => {
    setQuestions((current) => {
      const nextQuestions = current.filter((question) => question.localId !== localId);
      return nextQuestions.length > 0 ? sortQuestions(nextQuestions) : [];
    });
  };

  const handleOpenDeleteQuestion = (question: QuestionDraft) => {
    setSelectedQuestionForDelete(question);
    setConfirmDeleteOpen(true);
  };

  const handleCloseDeleteQuestion = () => {
    setConfirmDeleteOpen(false);
    setSelectedQuestionForDelete(null);
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestionForDelete) return;

    if (isEditing && templateId && selectedQuestionForDelete.id) {
      try {
        setLoadingQuestionAction(true);
        await deleteSatisfactionSurveyQuestion(templateId, selectedQuestionForDelete.id);
        await loadTemplateData(false, true);
        setMsgSuccess("Pergunta removida com sucesso.");
      } catch {
        setMsgError("Nao foi possivel remover a pergunta.");
      } finally {
        setLoadingQuestionAction(false);
        handleCloseDeleteQuestion();
      }
      return;
    }

    removeQuestion(selectedQuestionForDelete.localId);
    handleCloseDeleteQuestion();
  };

  const payloadPreview = useMemo(() => ({
    titulo,
    descricao,
    mensagem,
    active,
    questions: [...questions]
      .sort((a, b) => a.ordem - b.ordem)
      .map((question) => buildQuestionPayload(question)),
  }), [active, descricao, mensagem, questions, titulo]);

  const saveTemplate = async () => {
    try {
      setLoadingSave(true);
      setMsgError(null);

      const orderedQuestions = sortQuestions(questions);

      if (orderedQuestions.length === 0) {
        setMsgError("Adicione pelo menos uma pergunta antes de salvar o modelo.");
        setLoadingSave(false);
        return;
      }

      if (!validateQuestions(orderedQuestions)) {
        setLoadingSave(false);
        return;
      }

      if (isEditing && templateId) {
        await updateSatisfactionSurveyTemplate(templateId, {
          titulo,
          descricao,
          mensagem,
          active,
        });

        for (const question of orderedQuestions) {
          const payload = buildQuestionPayload(question);

          if (question.id) {
            await updateSatisfactionSurveyQuestion(templateId, question.id, payload);
          } else {
            await createSatisfactionSurveyQuestion(templateId, payload);
          }
        }

        setMsgSuccess("Modelo de pesquisa atualizado com sucesso.");
      } else {
        await createSatisfactionSurveyTemplate({
          titulo,
          descricao,
          mensagem,
          active,
          questions: orderedQuestions.map((question) => buildQuestionPayload(question)),
        });
        setMsgSuccess("Modelo de pesquisa cadastrado com sucesso.");
      }

      navigate("/modelos-pesquisa");
    } catch {
      setMsgError("Nao foi possivel salvar o modelo de pesquisa.");
    } finally {
      setLoadingSave(false);
    }
  };

  const renderSelectQuestionPreview = (
    question: SatisfactionSurveyQuestionPayload,
    options: Array<{ value: string | number; label: string }>,
  ) => {
    const selectOptions: OptionItem[] = options.map((option, index) => ({
      id: index + 1,
      nome: option.label,
    }));

    return (
      <SearchSelect
        label=""
        options={selectOptions}
        value={null}
        onChange={() => undefined}
        disabled
        paperClassname={stylesInput.paper}
        popperClassname={stylesInput.popper}
        labelClassname={stylesInput.labelSelect}
        inputClassname={stylesInput.inputSelect}
      />
    );
  };

  const renderPreviewQuestionField = (question: SatisfactionSurveyQuestionPayload) => {
    const questionId = question.ordem;

    if (question.tipo === "texto_livre") {
      return (
        <TextField
          size="small"
          variant="standard"
          fullWidth
          multiline
          minRows={3}
          disabled
          InputProps={{
            className: stylesInput.input,
            disableUnderline: true,
          }}
        />
      );
    }

    if (question.tipo === "escala") {
      return (
        <RadioGroup className={certStyles.scaleOptions}>
          {escalaOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled
              control={<Radio />}
              label={
                <span className={certStyles.scaleRadioLabel}>
                  {option.value} - {option.label}
                </span>
              }
            />
          ))}
        </RadioGroup>
      );
    }

    if (question.tipo === "escala_dez") {
      return (
        <div className={certStyles.starScale}>
          {Array.from({ length: 10 }, (_, index) => (
            <button key={index + 1} type="button" className={certStyles.starButton} disabled>
              <span className={certStyles.starButtonNumber}>{index + 1}</span>
              <span>☆</span>
            </button>
          ))}
        </div>
      );
    }

    if (question.tipo === "checkbox") {
      return (
        <div className={certStyles.checkboxGroup}>
          {(question.opcoes || []).map((option) => (
            <FormControlLabel
              key={`${questionId}-${option.value}`}
              disabled
              control={<Checkbox />}
              label={option.label}
            />
          ))}
        </div>
      );
    }

    if (question.tipo === "radio") {
      return (
        <RadioGroup className={certStyles.radioGroup}>
          {(question.opcoes || []).map((option) => (
            <FormControlLabel
              key={`${questionId}-${option.value}`}
              value={option.value}
              disabled
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      );
    }

    if (question.tipo === "lista_personalizada") {
      return renderSelectQuestionPreview(question, question.opcoes || []);
    }

    if (question.tipo === "tabela") {
      const realQuestion = questions.find((item) => item.ordem === question.ordem);
      const options = realQuestion?.id ? questionOptionsPreview[realQuestion.id] || [] : [];
      return renderSelectQuestionPreview(question, options);
    }

    return <Alert severity="info">Tipo de pergunta ainda nao suportado nesta tela.</Alert>;
  };

  if (loadingInitialData) {
    return (
      <Card className={styles.card}>
        <CardContent>
          <div className={styles.title}>
            {isEditing ? "Editar Modelo de Pesquisa" : "Cadastrar Modelo de Pesquisa"}
          </div>
          <div className={styles.loadingState}>
            <CircularProgress />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          {isEditing ? "Editar Modelo de Pesquisa" : "Cadastrar Modelo de Pesquisa"}
        </div>

        {msgSuccess && (
          <div className={stylesGeneral.mt4}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={stylesGeneral.mt4}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>Dados gerais</span>
          </div>

          <div className={stylesGrid.grid12}>
            <div className={stylesGrid.inputGroup10}>
              <label className={stylesInput.titleInput}>Titulo</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={titulo}
                onChange={(event) => setTitulo(event.target.value)}
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>

            <div className={stylesGrid.inputGroup2}>
              <label className={stylesInput.titleInput}>Status</label>
              <SearchSelect
                label=""
                options={statusOptions}
                value={active ? 1 : 2}
                onChange={(id) => setActive(id === 1)}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>

            <div className={stylesGrid.inputGroupFull}>
              <label className={stylesInput.titleInput}>Descricao</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={descricao}
                onChange={(event) => setDescricao(event.target.value)}
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>

            <div className={stylesGrid.inputGroupFull}>
              <label className={stylesInput.titleInput}>Mensagem</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                multiline
                minRows={3}
                value={mensagem}
                onChange={(event) => setMensagem(event.target.value)}
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>Perguntas</span>
            <Button
              variant="contained"
              className={stylesButton.buttonSquarePrimary}
              startIcon={<Add />}
              onClick={openNewQuestionModal}
            >
              Nova pergunta
            </Button>
          </div>

          <div className={styles.questionList}>
            {questions.length === 0 ? (
              <Alert severity="info">Nenhuma pergunta adicionada ate o momento.</Alert>
            ) : (
              sortQuestions(questions).map((question) => (
              <div key={question.localId} className={styles.questionCard}>
                <div className={styles.questionCardHeader}>
                  <div>
                    <div className={styles.questionCardTitle}>Pergunta {question.ordem}</div>
                  </div>

                  <div className={styles.questionActions}>
                    <span className={styles.questionBadge}>
                      {questionTypeOptions.find((option) => option.value === question.tipo)?.label || question.tipo}
                    </span>
                    <IconButton onClick={() => openEditQuestionModal(question)}
                      size="small"
                      color="primary"
                      aria-label="Editar pergunta"   
                    >
                      <EditOutlined />
                    </IconButton>
                    <IconButton onClick={() => handleOpenDeleteQuestion(question)}
                      size="small"
                      color="error"
                      aria-label="Excluir pergunta"  
                    >
                      <DeleteOutline />
                    </IconButton>
                  </div>
                </div>

                <div>
                    <div className={styles.questionName}>{question.nome || "Sem enunciado"}</div>
                </div>

                <div className={styles.questionMeta}>
                  <span>{question.obrigatorio ? "Obrigatória" : "Opcional"}</span>
                  <span>{question.active ? "Ativa" : "Inativa"}</span>
                  {isOptionQuestionType(question.tipo) ? (
                    <span>{question.opcoes.filter((option) => option.label.trim()).length} opções</span>
                  ) : null}
                  {isTableQuestionType(question.tipo) && question.config.source ? (
                    <span>Source: {question.config.source}</span>
                  ) : null}
                </div>
              </div>
              ))
            )}
          </div>
        </section>

        <section className={certStyles.sectionCard}>
          <div className={styles.sectionHeader}>
            <span className={certStyles.sectionTitle}>Prévia da pesquisa</span>
            <PreviewOutlined className={styles.previewIcon} />
          </div>

          <div>
            <strong>{titulo}</strong>
            {descricao ? <div>{descricao}</div> : null}
            {mensagem ? <div style={{ marginTop: 8 }}>{mensagem}</div> : null}
          </div>

          <div className={certStyles.surveyQuestionList}>
            {payloadPreview.questions.length === 0 ? (
              <Alert severity="info">A previa sera exibida aqui assim que a primeira pergunta for adicionada.</Alert>
            ) : (
              payloadPreview.questions.map((question, index) => (
              <div key={`${question.nome}-${index}`} className={certStyles.surveyQuestionCard}>
                <div className={certStyles.surveyQuestionHeader}>
                  <h3 className={certStyles.surveyQuestionTitle}>
                    {question.ordem}. {question.nome}
                  </h3>
                  {question.obrigatorio ? (
                    <span className={certStyles.requiredBadge}>Obrigatória</span>
                  ) : null}
                </div>
                {renderPreviewQuestionField(question)}
              </div>
              ))
            )}
          </div>
        </section>

        <section>
          <div className={styles.actions}>
            <Button
              variant="contained"
              className={stylesButton.buttonSquareSecondary}
              onClick={() => navigate("/modelos-pesquisa")}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              className={`${stylesButton.buttonSquarePrimary} ${loadingSave ? styles.disabledAction : ""}`}
              onClick={saveTemplate}
              disabled={loadingSave}
            >
              {loadingSave ? "Salvando..." : isEditing ? "Atualizar modelo" : "Salvar modelo"}
            </Button>
          </div>
        </section>

        <Dialog
          open={questionModalOpen}
          onClose={closeQuestionModal}
          maxWidth="md"
          fullWidth
          PaperProps={{ className: styles.modalPaper }}
        >
          <DialogTitle className={styles.modalHeader}>
            <div className={styles.modalHeaderContent}>
              <span>{editingQuestionLocalId ? "Editar pergunta" : "Nova pergunta"}</span>
              <IconButton onClick={closeQuestionModal} className={styles.modalCloseButton}>
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent dividers className={styles.modalContent}>
            {questionDraft ? (
              <div className={styles.modalBody}>
                {!editingQuestionLocalId && questionModalStep === "select-type" ? (
                  <div className={styles.typeSelectorSection}>
                    <div className={styles.typeSelectorTitle}>Escolha um tipo de pergunta</div>
                    <div className={styles.typeSelectorGrid}>
                      {questionTypeOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={styles.typeSelectorCard}
                          onClick={() => selectQuestionType(option.value)}
                        >
                          <span className={styles.typeSelectorIcon}>{getQuestionTypeIcon(option.value)}</span>
                          <span className={styles.typeSelectorLabel}>{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                <div className={stylesGrid.grid12}>
                  <div className={stylesGrid.inputGroupFull}>
                    <label className={stylesInput.titleInput}>Enunciado da pergunta</label>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      multiline
                      minRows={3}
                      value={questionDraft.nome}
                      onChange={(event) =>
                        updateQuestionDraft((current) => ({
                          ...current,
                          nome: event.target.value,
                        }))
                      }
                      InputProps={{ className: stylesInput.input, disableUnderline: true }}
                    />
                  </div>
                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Ordem</label>
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      fullWidth
                      value={questionDraft.ordem}
                      onChange={(event) =>
                        updateQuestionDraft((current) => ({
                          ...current,
                          ordem: Number(event.target.value) || 1,
                        }))
                      }
                      InputProps={{ className: stylesInput.input, disableUnderline: true }}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Status</label>
                    <SearchSelect
                      label=""
                      options={statusOptions}
                      value={questionDraft.active ? 1 : 2}
                      onChange={(id) =>
                        updateQuestionDraft((current) => ({
                          ...current,
                          active: id === 1,
                        }))
                      }
                      paperClassname={stylesInput.paper}
                      popperClassname={stylesInput.popper}
                      labelClassname={stylesInput.labelSelect}
                      inputClassname={stylesInput.inputSelect}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup2}>
                    <label className={stylesInput.titleInput}>Obrigatória</label>
                    <SearchSelect
                      label=""
                      options={yesNoOptions}
                      value={questionDraft.obrigatorio ? 1 : 2}
                      onChange={(id) =>
                        updateQuestionDraft((current) => ({
                          ...current,
                          obrigatorio: id === 1,
                        }))
                      }
                      paperClassname={stylesInput.paper}
                      popperClassname={stylesInput.popper}
                      labelClassname={stylesInput.labelSelect}
                      inputClassname={stylesInput.inputSelect}
                    />
                  </div>

                  <div className={stylesGrid.inputGroup3}>
                    <label className={stylesInput.titleInput}>Tipo</label>
                    <div className={styles.typeLockedField}>
                      <span className={styles.typeLockedIcon}>{getQuestionTypeIcon(questionDraft.tipo)}</span>
                      <span>
                        {questionTypeOptions.find((option) => option.value === questionDraft.tipo)?.label || questionDraft.tipo}
                      </span>
                    </div>
                  </div>

                  
                </div>

                {isOptionQuestionType(questionDraft.tipo) && (
                  <div className={styles.subSection}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.subSectionTitle}>Opções</span>
                      
                    </div>

                    {questionDraft.opcoes.map((option) => (
                      <div key={option.localId} className={styles.optionRow}>
                        <div className={stylesGrid.grid12}>
                          <div className={stylesGrid.inputGroup10}>
                            <label className={stylesInput.titleInput}>Label</label>
                            <TextField
                              size="small"
                              variant="standard"
                              fullWidth
                              value={option.label}
                              onChange={(event) => updateDraftOption(option.localId, event.target.value)}
                              InputProps={{ className: stylesInput.input, disableUnderline: true }}
                            />
                          </div>

                          <div className={`${stylesGrid.inputGroup2} ${stylesGrid.itemsEnd}`}>
                            <Button
                              variant="contained"
                              className={stylesButton.buttonSquareDanger}
                              onClick={() => removeDraftOption(option.localId)}
                            >
                              Remover
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className={styles.optionActions}>
                      <Button
                        variant="contained"
                        className={stylesButton.buttonSquarePrimary}
                        startIcon={<Add />}
                        onClick={addDraftOption}
                      >
                        Adicionar opção
                      </Button>

                    </div>
                  </div>
                )}

                {isTableQuestionType(questionDraft.tipo) && (
                  <QuestionTableConfigSection
                    question={questionDraft}
                    sourceColumnsByTable={sourceColumnsByTable}
                    loadingColumnsByTable={loadingColumnsByTable}
                    loadSourceColumns={loadSourceColumns}
                    updateQuestionDraft={updateQuestionDraft}
                    addDraftFilter={addDraftFilter}
                    updateDraftFilter={updateDraftFilter}
                    removeDraftFilter={removeDraftFilter}
                  />
                )}
                  </>
                )}
              </div>
            ) : null}
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            {!editingQuestionLocalId && questionModalStep === "edit" ? (
              <Button
                variant="contained"
                className={stylesButton.buttonSquareSecondary}
                onClick={() => setQuestionModalStep("select-type")}
              >
                Voltar
              </Button>
            ) : null}
            <Button variant="contained" className={stylesButton.buttonSquareSecondary} onClick={closeQuestionModal}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              className={stylesButton.buttonSquarePrimary}
              onClick={saveQuestionModal}
              disabled={loadingQuestionAction || !questionDraft || (!editingQuestionLocalId && questionModalStep !== "edit")}
            >
              {loadingQuestionAction ? "Salvando..." : "Salvar pergunta"}
            </Button>
          </DialogActions>
        </Dialog>
        <ConfirmDialog
          open={confirmDeleteOpen}
          loading={loadingQuestionAction}
          onClose={handleCloseDeleteQuestion}
          onConfirm={handleDeleteQuestion}
          title="Remover pergunta"
          message={`Deseja remover a pergunta "${selectedQuestionForDelete?.nome || ""}"?`}
          titleButtonConfirm="Remover"
          titleLoadButton="Removendo..."
        />
      </CardContent>
    </Card>
  );
};

const QuestionTableConfigSection = ({
  question,
  sourceColumnsByTable,
  loadingColumnsByTable,
  loadSourceColumns,
  updateQuestionDraft,
  addDraftFilter,
  updateDraftFilter,
  removeDraftFilter,
}: {
  question: QuestionDraft;
  sourceColumnsByTable: Partial<Record<SatisfactionSurveySourceTable, SatisfactionSurveySourceColumn[]>>;
  loadingColumnsByTable: Partial<Record<SatisfactionSurveySourceTable, boolean>>;
  loadSourceColumns: (source: SatisfactionSurveySourceTable) => Promise<void>;
  updateQuestionDraft: (updater: (current: QuestionDraft) => QuestionDraft) => void;
  addDraftFilter: () => void;
  updateDraftFilter: (
    filterLocalId: string,
    field: "field" | "operator" | "value",
    value: string,
  ) => void;
  removeDraftFilter: (filterLocalId: string) => void;
}) => {
  const sourceColumnOptions: OptionItem[] = (question.config.source
    ? sourceColumnsByTable[question.config.source] || []
    : []
  ).map((column, index) => ({
    id: index + 1,
    nome: column.name,
  }));

  const fieldOptionIdByColumnName = (value: string) => getOptionIdByName(sourceColumnOptions, value);
  const getColumnNameByOptionId = (id: number | null) => getOptionNameById(sourceColumnOptions, id) || "";

  useEffect(() => {
    if (question.config.source && !sourceColumnsByTable[question.config.source] && !loadingColumnsByTable[question.config.source]) {
      void loadSourceColumns(question.config.source);
    }
  }, [question.config.source, sourceColumnsByTable, loadingColumnsByTable, loadSourceColumns]);

  return (
    <div className={styles.subSection}>
      <span className={styles.subSectionTitle}>Configuração da tabela</span>

      <div className={stylesGrid.grid12}>
        <div className={stylesGrid.inputGroup4}>
          <label className={stylesInput.titleInput}>Source</label>
          <SearchSelect
            label=""
            options={tableSourceSelectOptions}
            value={getOptionIdByName(tableSourceSelectOptions, question.config.source)}
            onChange={(id) =>
              updateQuestionDraft((current) => ({
                ...current,
                config: {
                  ...current.config,
                  source: (getOptionNameById(tableSourceSelectOptions, id) as SatisfactionSurveySourceTable | null) || "",
                  value_column: "",
                  label_column: "",
                  order_by: "",
                },
              }))
            }
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSelect}
          />
        </div>

        <div className={stylesGrid.inputGroup2}>
          <label className={stylesInput.titleInput}>Limit</label>
          <TextField
            type="number"
            size="small"
            variant="standard"
            fullWidth
            value={question.config.limit}
            onChange={(event) =>
              updateQuestionDraft((current) => ({
                ...current,
                config: {
                  ...current.config,
                  limit: event.target.value,
                },
              }))
            }
            InputProps={{ className: stylesInput.input, disableUnderline: true }}
          />
        </div>

        <div className={stylesGrid.inputGroup3}>
          <label className={stylesInput.titleInput}>Coluna value</label>
          <SearchSelect
            label=""
            options={sourceColumnOptions}
            value={fieldOptionIdByColumnName(question.config.value_column)}
            onChange={(id) =>
              updateQuestionDraft((current) => ({
                ...current,
                config: {
                  ...current.config,
                  value_column: getColumnNameByOptionId(id),
                },
              }))
            }
            disabled={!question.config.source || loadingColumnsByTable[question.config.source]}
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSelect}
          />
        </div>

        <div className={stylesGrid.inputGroup3}>
          <label className={stylesInput.titleInput}>Coluna label</label>
          <SearchSelect
            label=""
            options={sourceColumnOptions}
            value={fieldOptionIdByColumnName(question.config.label_column)}
            onChange={(id) =>
              updateQuestionDraft((current) => ({
                ...current,
                config: {
                  ...current.config,
                  label_column: getColumnNameByOptionId(id),
                },
              }))
            }
            disabled={!question.config.source || loadingColumnsByTable[question.config.source]}
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSelect}
          />
        </div>

        <div className={stylesGrid.inputGroup2}>
          <label className={stylesInput.titleInput}>Order by</label>
          <SearchSelect
            label=""
            options={sourceColumnOptions}
            value={fieldOptionIdByColumnName(question.config.order_by)}
            onChange={(id) =>
              updateQuestionDraft((current) => ({
                ...current,
                config: {
                  ...current.config,
                  order_by: getColumnNameByOptionId(id),
                },
              }))
            }
            disabled={!question.config.source || loadingColumnsByTable[question.config.source]}
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSelect}
          />
        </div>

        <div className={stylesGrid.inputGroup2}>
          <label className={stylesInput.titleInput}>Direção</label>
          <SearchSelect
            label=""
            options={orderDirectionOptions}
            value={getOptionIdByName(orderDirectionOptions, question.config.order_direction)}
            onChange={(id) =>
              updateQuestionDraft((current) => ({
                ...current,
                config: {
                  ...current.config,
                  order_direction: (getOptionNameById(orderDirectionOptions, id) as "asc" | "desc" | null) || "asc",
                },
              }))
            }
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            labelClassname={stylesInput.labelSelect}
            inputClassname={stylesInput.inputSelect}
          />
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <span className={styles.subSectionTitle}>Filtros</span>
        <Button
          variant="contained"
          className={stylesButton.buttonSquarePrimary}
          startIcon={<Add />}
          onClick={addDraftFilter}
        >
          Adicionar filtro
        </Button>
      </div>

      {question.config.filters.map((filter) => (
        <div key={filter.localId} className={styles.filterRow}>
          <div className={stylesGrid.grid12}>
            <div className={stylesGrid.inputGroup4}>
              <label className={stylesInput.titleInput}>Field</label>
              <SearchSelect
                label=""
                options={sourceColumnOptions}
                value={fieldOptionIdByColumnName(filter.field)}
                onChange={(id) => updateDraftFilter(filter.localId, "field", getColumnNameByOptionId(id))}
                disabled={!question.config.source || loadingColumnsByTable[question.config.source]}
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>

            <div className={stylesGrid.inputGroup2}>
              <label className={stylesInput.titleInput}>Operador</label>
              <SearchSelect
                label=""
                options={filterOperatorSelectOptions}
                value={getOptionIdByName(filterOperatorSelectOptions, filter.operator)}
                onChange={(id) =>
                  updateDraftFilter(
                    filter.localId,
                    "operator",
                    (getOptionNameById(filterOperatorSelectOptions, id) as SatisfactionQuestionFilterOperator | null) || filter.operator,
                  )
                }
                paperClassname={stylesInput.paper}
                popperClassname={stylesInput.popper}
                labelClassname={stylesInput.labelSelect}
                inputClassname={stylesInput.inputSelect}
              />
            </div>

            <div className={stylesGrid.inputGroup4}>
              <label className={stylesInput.titleInput}>
                Valor {filter.operator === "in" ? "(separado por virgula)" : ""}
              </label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={filter.value}
                onChange={(event) => updateDraftFilter(filter.localId, "value", event.target.value)}
                InputProps={{ className: stylesInput.input, disableUnderline: true }}
              />
            </div>

            <div className={`${stylesGrid.inputGroup2} ${stylesGrid.itemsEnd}`}>
              <Button
                variant="contained"
                className={stylesButton.buttonSquareDanger}
                onClick={() => removeDraftFilter(filter.localId)}
              >
                Remover
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CadastrarModeloPesquisa;
